"use client"

import { useState, useEffect } from "react"
import { Bar, BarChart, Line, LineChart, Pie, PieChart, ResponsiveContainer, XAxis, YAxis, Tooltip, Legend, Cell, CartesianGrid } from "recharts"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Loader2 } from "lucide-react"
import { useToast } from "./ui/use-toast"
import { supabase } from "@/utils/supabase/client"
import { useAuth } from "@/contexts/auth-context"
import { format, subDays, differenceInDays } from "date-fns"
import { Payment, PaymentWithRelations } from "@/types"

const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042"];

interface RevenueTrendItem {
    date: string;
    revenue: number;
}
interface PaymentMethodItem {
    name: string;
    value: number;
}
interface MonthlyRevenueItem {
    name: string;
    total: number;
}
interface OutstandingStats {
    total: number;
    buckets: {
        '0-30': number;
        '31-60': number;
        '61-90': number;
        '90+': number;
    };
}
interface DashboardData {
  todaysCollection: number;
  outstandingAmount: number;
  collectionRate: number;
  revenueTrend: RevenueTrendItem[];
  paymentMethods: PaymentMethodItem[];
  monthlyRevenue: MonthlyRevenueItem[];
  outstandingStats: OutstandingStats;
}

export function FinancialDashboard() {
  const [data, setData] = useState<DashboardData | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const { toast } = useToast()
  const { currentOrganization } = useAuth()

  useEffect(() => {
    const fetchFinancialData = async () => {
      if (!currentOrganization) return;
      setIsLoading(true);

      try {
        const { data: payments, error } = await supabase
          .from('payments')
          .select(`
            *,
            appointments (
              *,
              patients (*)
            )
          `)
          .eq('organization_id', currentOrganization.id);
        
        if (error) throw error;
        
        if (payments) {
          const typedPayments = payments as unknown as PaymentWithRelations[];
          if (typedPayments.length === 0) return;

          const today = format(new Date(), 'yyyy-MM-dd');
          const todaysRevenue = typedPayments
            .filter(p => format(new Date(p.created_at), 'yyyy-MM-dd') === today && p.status === 'completed')
            .reduce((acc, p) => acc + p.amount_paid, 0);

          const outstandingBalance = typedPayments
            .reduce((acc, p) => acc + (p.amount_due || 0), 0);
          const totalBilled = typedPayments.reduce((acc, p) => acc + p.amount_paid + (p.amount_due || 0), 0);

          const last7Days = Array.from({ length: 7 }, (_, i) => {
            const date = subDays(new Date(), i);
            const dateStr = format(date, 'yyyy-MM-dd');
            const dailyRevenue = typedPayments
              .filter(p => format(new Date(p.created_at), 'yyyy-MM-dd') === dateStr && p.status === 'completed')
              .reduce((acc, p) => acc + p.amount_paid, 0);
            return { date: format(date, 'MMM d'), revenue: dailyRevenue };
          }).reverse();

          const paymentMethodsDistribution = typedPayments.reduce((acc, p) => {
            const existing = acc.find(item => item.name === p.payment_method);
            if (existing) {
              existing.value += p.amount_paid;
            } else if (p.payment_method) {
              acc.push({ name: p.payment_method, value: p.amount_paid });
            }
            return acc;
          }, [] as { name: string, value: number }[]);

          const monthlyTrends = typedPayments.reduce((acc, p) => {
            const month = format(new Date(p.created_at), 'MMM yyyy');
            acc[month] = (acc[month] || 0) + p.amount_paid;
            return acc;
          }, {} as Record<string, number>);

          const agingSummary = typedPayments.filter(p => p.amount_due && p.amount_due > 0).map(p => {
            const balance = p.amount_due || 0;
            const age = differenceInDays(new Date(), new Date(p.created_at));
            return { patient: p.appointments?.patients?.name || 'N/A', balance, age };
          });

          setData({
            todaysCollection: todaysRevenue,
            outstandingAmount: outstandingBalance,
            collectionRate: totalBilled > 0 ? ((totalBilled - outstandingBalance) / totalBilled) : 0,
            revenueTrend: last7Days,
            paymentMethods: paymentMethodsDistribution,
            monthlyRevenue: Object.entries(monthlyTrends).map(([name, total]) => ({ name, total })),
            outstandingStats: {
              total: outstandingBalance,
              buckets: {
                '0-30': agingSummary.filter(a => a.age <= 30).reduce((sum, a) => sum + a.balance, 0),
                '31-60': agingSummary.filter(a => a.age > 30 && a.age <= 60).reduce((sum, a) => sum + a.balance, 0),
                '61-90': agingSummary.filter(a => a.age > 60 && a.age <= 90).reduce((sum, a) => sum + a.balance, 0),
                '90+': agingSummary.filter(a => a.age > 90).reduce((sum, a) => sum + a.balance, 0),
              }
            }
          });
        }

      } catch (err: any) {
        toast({
          title: 'Error fetching financial data',
          description: err.message,
          variant: 'destructive'
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchFinancialData();
  }, [toast, currentOrganization]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!data) {
    return <p className="text-center text-muted-foreground mt-8">Could not load dashboard data.</p>;
  }

  return (
    <div className="space-y-6 mt-6">
        {/* Stat Cards */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <Card>
                <CardHeader><CardTitle>Today's Collection</CardTitle></CardHeader>
                <CardContent>
                    <div className="text-3xl font-bold">Rs. {data.todaysCollection.toLocaleString()}</div>
                </CardContent>
            </Card>
            <Card>
                <CardHeader><CardTitle>Total Outstanding</CardTitle></CardHeader>
                <CardContent>
                    <div className="text-3xl font-bold">Rs. {data.outstandingAmount.toLocaleString()}</div>
                </CardContent>
            </Card>
            <Card>
                <CardHeader><CardTitle>Collection Rate</CardTitle></CardHeader>
                <CardContent>
                    <div className="text-3xl font-bold">{((data.collectionRate || 0) * 100).toFixed(2)}%</div>
                </CardContent>
            </Card>
             <Card>
                <CardHeader><CardTitle>Pending Approvals</CardTitle></CardHeader>
                <CardContent>
                    <div className="text-3xl font-bold">0</div>
                </CardContent>
            </Card>
        </div>

        {/* Charts */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-7">
            <Card className="lg:col-span-4">
                <CardHeader><CardTitle>Revenue Trend (Last 7 Days)</CardTitle></CardHeader>
                <CardContent className="pl-2">
                    <ResponsiveContainer width="100%" height={350}>
                        <LineChart data={data.revenueTrend}>
                            <XAxis dataKey="date" stroke="#888888" fontSize={12} tickLine={false} axisLine={false} />
                            <YAxis stroke="#888888" fontSize={12} tickLine={false} axisLine={false} tickFormatter={(value) => `Rs.${value/1000}k`} />
                            <Tooltip />
                            <Line type="monotone" dataKey="revenue" stroke="#8884d8" activeDot={{ r: 8 }} />
                        </LineChart>
                    </ResponsiveContainer>
                </CardContent>
            </Card>
             <Card className="lg:col-span-3">
                <CardHeader><CardTitle>Payment Methods</CardTitle></CardHeader>
                <CardContent>
                     <ResponsiveContainer width="100%" height={350}>
                        <PieChart>
                            <Pie data={data.paymentMethods} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={120} fill="#8884d8">
                                 {data.paymentMethods.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                 ))}
                            </Pie>
                            <Tooltip />
                            <Legend />
                        </PieChart>
                    </ResponsiveContainer>
                </CardContent>
            </Card>
        </div>

        {/* New additions from other tabs */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
             <Card className="lg:col-span-1 bg-destructive/10 border-destructive">
                 <CardHeader><CardTitle>Total Outstanding</CardTitle></CardHeader>
                 <CardContent>
                     <div className="text-3xl font-bold">Rs. {(data.outstandingStats.total || 0).toLocaleString()}</div>
                 </CardContent>
             </Card>
             <Card>
                 <CardHeader><CardTitle>0-30 Days</CardTitle></CardHeader>
                 <CardContent>
                     <div className="text-2xl font-bold">Rs. {(data.outstandingStats.buckets['0-30'] || 0).toLocaleString()}</div>
                 </CardContent>
             </Card>
             <Card>
                 <CardHeader><CardTitle>31-60 Days</CardTitle></CardHeader>
                 <CardContent>
                     <div className="text-2xl font-bold">Rs. {(data.outstandingStats.buckets['31-60'] || 0).toLocaleString()}</div>
                 </CardContent>
             </Card>
              <Card>
                 <CardHeader><CardTitle>61-90 Days</CardTitle></CardHeader>
                 <CardContent>
                     <div className="text-2xl font-bold">Rs. {(data.outstandingStats.buckets['61-90'] || 0).toLocaleString()}</div>
                 </CardContent>
             </Card>
              <Card>
                 <CardHeader><CardTitle>90+ Days</CardTitle></CardHeader>
                 <CardContent>
                     <div className="text-2xl font-bold">Rs. {(data.outstandingStats.buckets['90+'] || 0).toLocaleString()}</div>
                 </CardContent>
             </Card>
        </div>
        
        <Card>
            <CardHeader><CardTitle>Monthly Revenue Trend</CardTitle></CardHeader>
            <CardContent>
                <ResponsiveContainer width="100%" height={400}>
                    <BarChart data={data.monthlyRevenue}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="total" fill="#ffc658" name="Monthly Revenue"/>
                    </BarChart>
                </ResponsiveContainer>
            </CardContent>
        </Card>
    </div>
  )
} 